import type { ComponentType } from "react";
import { useEffect, useState } from "react";
import { LuLink, LuMessageSquareReply, LuSmilePlus } from "react-icons/lu";

import { EditorActionAudio } from "@/components/app/editor-v3/actions/editor-action-audio";
import { EditorActionEmbed } from "@/components/app/editor-v3/actions/editor-action-embed";
import { EditorActionEmoji } from "@/components/app/editor-v3/actions/editor-action-emoji";
import { EditorActionGif } from "@/components/app/editor-v3/actions/editor-action-gif";
import { EditorActionSubmit } from "@/components/app/editor-v3/actions/editor-action-submit";
import { EditorActionVideo } from "@/components/app/editor-v3/actions/editor-action-video";
import {
  EDITOR_MEDIA_TAB,
  EDITOR_MODE,
} from "@/components/app/editor-v3/constants";
import { EditorEmbedActionDetails } from "@/components/app/editor-v3/editor-embed-action-details";
import { EditorEmojiPicker } from "@/components/app/editor-v3/editor-emoji-picker";
import { EditorToolbar } from "@/components/app/editor-v3/toolbar";
import { HorizontalScrollRail } from "@/components/shared/horizontal-scroll-rail";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils/lib";

import { EditorActionFormatting } from "../actions/editor-action-formatting";
import { EditorActionPhoto } from "../actions/editor-action-photo";
import {
  EditorFooterActionButton,
  EditorFooterActionContainer,
} from "../editor-action-utils";
import {
  EditorFieldContextProvider,
  useEditorContext,
} from "../editor-context";
import { EditorFileDropArea } from "../editor-file-drop-area";
import { EditorMediaContainer } from "../editor-media-container";
import { TheEditor } from "../the-editor";

const TAB = {
  ...EDITOR_MEDIA_TAB,
  body: "body",
};

const TRANSIENT_EDITOR_TABS = new Set<string>([
  EDITOR_MEDIA_TAB.Embed,
  EDITOR_MEDIA_TAB.Emoji,
]);

type OverlayEditorTab = {
  icon: ComponentType<{ className?: string }>;
  label: string;
  value: string;
};

export const CommentOverlayEditor = ({
  to,
  submitTitle = "Reply",
}: {
  to: string;
  submitTitle?: string;
}) => {
  const { mode, activeMediaTab, setEditorContext, lastFocusedField } =
    useEditorContext();
  const [tab, setTab] = useState(TAB.body);
  const showEmbedTab = activeMediaTab === TAB.Embed || tab === TAB.Embed;
  const showEmojiTab = activeMediaTab === TAB.Emoji || tab === TAB.Emoji;
  const overlayTabs: OverlayEditorTab[] = [
    {
      icon: LuMessageSquareReply,
      label: `Reply to ${to}...`,
      value: TAB.body,
    },
    ...(showEmbedTab
      ? [{ icon: LuLink, label: "Embed", value: TAB.Embed }]
      : []),
    ...(showEmojiTab
      ? [{ icon: LuSmilePlus, label: "Emoji", value: TAB.Emoji }]
      : []),
  ];

  useEffect(() => {
    if (activeMediaTab) {
      setTab(activeMediaTab);
      return;
    }

    if (TRANSIENT_EDITOR_TABS.has(tab)) {
      setTab("body");
    }
  }, [activeMediaTab, tab]);

  const handleTabChange = (nextTab: string | number | null) => {
    if (typeof nextTab !== "string") {
      return;
    }

    setTab(nextTab);
    setEditorContext({
      activeMediaTab: TRANSIENT_EDITOR_TABS.has(nextTab)
        ? (nextTab as EDITOR_MEDIA_TAB)
        : null,
    });
  };

  return (
    <Tabs
      value={tab}
      onValueChange={handleTabChange}
      defaultValue={TAB.body}
      className="flex size-full min-h-0 flex-col bg-background"
    >
      <TabsList
        className="
          unforce-radius z-20 flex w-full border-t-0 border-b
          border-border bg-background/95 p-2 pr-14 backdrop-blur-md
        "
      >
        <HorizontalScrollRail
          className="min-w-0 flex-1"
          contentClassName="gap-1 p-px"
          scrollStep={180}
        >
          {overlayTabs.map((item) => {
            const Icon = item.icon;

            return (
              <TabsTrigger
                key={item.value}
                value={item.value}
                className={cn(
                  `
                    inline-flex shrink-0 items-center gap-2 rounded-full
                    border border-transparent bg-background p-2 text-sm
                    text-muted transition-colors
                    hover:border-border hover:text-foreground
                    data-active:border-primary data-active:bg-primary/10
                    data-active:text-foreground
                  `,
                  tab === item.value &&
                    "border-primary bg-primary/10 text-foreground",
                )}
              >
                <Icon className="size-4 shrink-0" />
                <span className="max-w-[170px] truncate">{item.label}</span>
              </TabsTrigger>
            );
          })}
        </HorizontalScrollRail>
      </TabsList>

      <TabsContent
        value="body"
        className="flex size-full min-h-0 grow flex-col p-2"
      >
        <EditorFieldContextProvider
          name="body"
          placeholder="What's on your mind?"
        >
          <div
            className="
              h-0 w-full shrink-0 overflow-hidden opacity-0
              transition-[opacity,height] duration-500
              will-change-auto in-data-[mode=rich]:h-auto in-data-[mode=rich]:opacity-100
            "
          >
            <EditorToolbar />
          </div>
          <div className="min-h-0 grow overflow-y-auto">
            <TheEditor />
          </div>
          <EditorFieldContextProvider name="media">
            <EditorMediaContainer />
            <EditorFileDropArea targetLabel="this reply" />
          </EditorFieldContextProvider>
        </EditorFieldContextProvider>
      </TabsContent>

      <TabsContent
        className="min-h-0 grow overflow-y-auto p-2 pb-3"
        value={TAB.Embed}
      >
        <EditorFieldContextProvider name="poll">
          <EditorEmbedActionDetails />
        </EditorFieldContextProvider>
      </TabsContent>

      <TabsContent
        className="
          min-h-0 grow p-2 pb-3
          [&>div]:h-full
          [&>div>aside]:h-full!
        "
        value={TAB.Emoji}
      >
        <EditorFieldContextProvider name={lastFocusedField || "body"}>
          <EditorEmojiPicker />
        </EditorFieldContextProvider>
      </TabsContent>

      <div className="unforce-radius flex p-2">
        <div className="grow">
          <EditorFooterActionContainer>
            <EditorFooterActionButton
              action={EditorActionFormatting}
              tooltipText="Formatting"
              active={mode === EDITOR_MODE.RICH}
            />
            <EditorFieldContextProvider name="media">
              <EditorFooterActionButton
                action={EditorActionPhoto}
                tooltipText="Upload Images"
              />
            </EditorFieldContextProvider>
            <EditorFieldContextProvider name="media">
              <EditorFooterActionButton
                action={EditorActionVideo}
                tooltipText="Upload Videos"
              />
            </EditorFieldContextProvider>
            <EditorFieldContextProvider name="media">
              <EditorFooterActionButton
                action={EditorActionAudio}
                tooltipText="Upload Audio"
                side="top"
              />
            </EditorFieldContextProvider>
            <EditorFieldContextProvider name="body">
              <EditorFooterActionButton
                action={EditorActionGif}
                tooltipText="Add GIF"
              />
            </EditorFieldContextProvider>
            <EditorFooterActionButton
              action={EditorActionEmbed}
              tooltipText="Manage embedded media"
              active={activeMediaTab === EDITOR_MEDIA_TAB.Embed}
            />
            <EditorFieldContextProvider name="body">
              <EditorFooterActionButton
                action={EditorActionEmoji}
                tooltipText="Add emoji"
                active={activeMediaTab === EDITOR_MEDIA_TAB.Emoji}
                className="
                  hidden
                  md:inline-flex
                "
              />
            </EditorFieldContextProvider>
          </EditorFooterActionContainer>
        </div>

        <div className="ml-auto flex gap-2">
          <div>
            <EditorActionSubmit className="px-3" title={submitTitle} />
          </div>
        </div>
      </div>
    </Tabs>
  );
};
